import { useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import SEOHelmet from '@/components/SEOHelmet';
import { LeadStatsCards } from '@/components/admin/leads/LeadStatsCards';
import { LeadCharts } from '@/components/admin/leads/LeadCharts';
import { LeadFilters } from '@/components/admin/leads/LeadFilters';
import { LeadsTable } from '@/components/admin/leads/LeadsTable';
import { ExportButton } from '@/components/admin/leads/ExportButton';
import { useLeadsData } from '@/hooks/useLeadsData';
import { LeadFilters as LeadFiltersType } from '@/types/leads.types';
import { Skeleton } from '@/components/ui/skeleton';
import { Card } from '@/components/ui/card';
import { FileText } from 'lucide-react';

const LeadsDashboard = () => {
  const [filters, setFilters] = useState<LeadFiltersType>({
    dateFrom: null,
    dateTo: null,
    practiceType: 'all',
    consultationFilter: 'all',
    statusFilter: 'all',
  });

  const { leads, loading } = useLeadsData(filters);

  const handleReset = () => {
    setFilters({
      dateFrom: null,
      dateTo: null,
      practiceType: 'all',
      consultationFilter: 'all',
      statusFilter: 'all',
    });
  };

  return (
    <AdminLayout>
      <SEOHelmet
        title="Lead-Verwaltung | Admin"
        description="Verwaltung und Analyse der Checklist-Downloads"
      />

      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Lead-Verwaltung</h1>
            <p className="text-muted-foreground mt-2">
              Übersicht und Analyse aller Checklist-Downloads
            </p>
          </div>
          {!loading && leads.length > 0 && <ExportButton leads={leads} />}
        </div>

        {/* Stats Cards */}
        {loading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        ) : (
          <LeadStatsCards leads={leads} />
        )}

        {/* Filters */}
        <LeadFilters
          dateFrom={filters.dateFrom}
          dateTo={filters.dateTo}
          practiceType={filters.practiceType}
          consultationFilter={filters.consultationFilter}
          statusFilter={filters.statusFilter}
          onDateFromChange={(date) => setFilters({ ...filters, dateFrom: date })}
          onDateToChange={(date) => setFilters({ ...filters, dateTo: date })}
          onPracticeTypeChange={(value) => setFilters({ ...filters, practiceType: value })}
          onConsultationFilterChange={(value) =>
            setFilters({ ...filters, consultationFilter: value })
          }
          onStatusFilterChange={(value) => setFilters({ ...filters, statusFilter: value })}
          onReset={handleReset}
        />

        {/* Charts */}
        {loading ? (
          <div className="grid gap-4 md:grid-cols-2">
            <Skeleton className="h-[350px]" />
            <Skeleton className="h-[350px]" />
          </div>
        ) : leads.length > 0 ? (
          <LeadCharts leads={leads} />
        ) : null}

        {/* Table */}
        {loading ? (
          <Skeleton className="h-[400px]" />
        ) : leads.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">Noch keine Leads</h3>
            <p className="text-muted-foreground">
              Sobald Besucher die Checkliste herunterladen, erscheinen sie hier.
            </p>
          </Card>
        ) : (
          <LeadsTable leads={leads} />
        )}
      </div>
    </AdminLayout>
  );
};

export default LeadsDashboard;
